import React, { useState, useMemo } from 'react';
import {
    Box, Typography, Container, Paper,
    Grid, Button, Card, CardContent, Divider,
    Alert, Chip, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, IconButton, LinearProgress, useTheme
} from '@mui/material';
import {
    Speed as SpeedIcon,
    Storage as StorageIcon,
    Memory as MemoryIcon,
    Group as GroupIcon,
    Edit as EditIcon,
    Close as CloseIcon,
    Settings as SettingsIcon,
    TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';

// --- MOCK DATA ---
const mockQuotas = [
    { id: 1, department: 'Marketing', cpuLimit: 12, cpuUsed: 8.5, memoryLimit: 64, memoryUsed: 55, storageLimit: 2048, storageUsed: 1500, users: 45, status: 'Healthy' },
    { id: 2, department: 'Engineering', cpuLimit: 48, cpuUsed: 45, memoryLimit: 256, memoryUsed: 210, storageLimit: 4096, storageUsed: 3900, users: 150, status: 'Warning' },
    { id: 3, department: 'Finance', cpuLimit: 8, cpuUsed: 3, memoryLimit: 32, memoryUsed: 15, storageLimit: 1024, storageUsed: 500, users: 30, status: 'Healthy' },
    { id: 4, department: 'HR', cpuLimit: 4, cpuUsed: 3.9, memoryLimit: 16, memoryUsed: 15.5, storageLimit: 512, storageUsed: 500, users: 20, status: 'Critical' },
];

// --- HELPER COMPONENTS ---

/**
 * Renders a progress bar indicating usage percentage.
 */
const UsageProgressBar = ({ used, limit, theme }) => {
    const percentage = (used / limit) * 100;
    let color = 'success';
    if (percentage > 85) color = 'error';
    else if (percentage > 70) color = 'warning';

    return (
        <Box sx={{ width: '100%' }}>
            <LinearProgress variant="determinate" value={percentage} color={color} sx={{ height: 6, borderRadius: 5, mb: 0.5 }} />
            <Typography variant="caption" color="text.secondary">
                {used.toFixed(1)} / {limit} (units) - {percentage.toFixed(0)}%
            </Typography>
        </Box>
    );
};

/**
 * Renders a summary card for a specific resource type.
 */
const SummaryCard = ({ title, icon, totalLimit, totalUsed, unit, color }) => {
    const theme = useTheme();
    const percentage = (totalUsed / totalLimit) * 100;

    return (
        <Card elevation={3}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{ p: 1, mr: 2, borderRadius: '50%', background: theme.palette[color].light, color: theme.palette[color].dark, display: 'flex' }}>
                        {icon}
                    </Box>
                    <Typography variant="h6" fontWeight={600} color="textPrimary">{title}</Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color={theme.palette[color].main}>
                    {totalUsed.toFixed(1)}
                    <Typography component="span" variant="h5" color="textSecondary" sx={{ ml: 1 }}>
                        {unit}
                    </Typography>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Total Limit: {totalLimit} {unit}
                </Typography>
                <LinearProgress 
                    variant="determinate" 
                    value={percentage} 
                    color={percentage > 85 ? 'error' : color} 
                    sx={{ mt: 2, height: 8, borderRadius: 4 }} 
                />
            </CardContent>
        </Card>
    );
};

// --- DIALOG FOR EDITING QUOTA ---

const EditQuotaDialog = ({ open, handleClose, quota, handleSave }) => {
    const [formData, setFormData] = useState(quota ? {
        cpuLimit: quota.cpuLimit,
        memoryLimit: quota.memoryLimit,
        storageLimit: quota.storageLimit,
    } : {});

    React.useEffect(() => {
        if (quota) {
            setFormData({
                cpuLimit: quota.cpuLimit,
                memoryLimit: quota.memoryLimit,
                storageLimit: quota.storageLimit,
            });
        }
    }, [quota]);

    const handleChange = (e) => {
        const value = e.target.type === 'number' ? parseFloat(e.target.value) : e.target.value;
        setFormData({ ...formData, [e.target.name]: value });
    };

    const handleInternalSave = () => {
        // Simple validation
        if (formData.cpuLimit <= 0 || formData.memoryLimit <= 0 || formData.storageLimit <= 0) {
            alert("Limits must be greater than zero.");
            return;
        }

        // Check if new limit is below current usage (a common real-world restriction)
        if (formData.cpuLimit < quota.cpuUsed || formData.memoryLimit < quota.memoryUsed || formData.storageLimit < quota.storageUsed) {
             alert("Cannot set a limit below the current resource usage.");
             return;
        }

        handleSave(quota.id, formData);
        handleClose();
    };

    if (!quota) return null;

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={700}>Edit Quota for {quota.department}</Typography>
                <IconButton onClick={handleClose}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <TextField
                    fullWidth
                    label={`CPU Limit (Used: ${quota.cpuUsed} cores)`}
                    name="cpuLimit"
                    type="number"
                    margin="normal"
                    value={formData.cpuLimit}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    fullWidth
                    label={`Memory Limit (Used: ${quota.memoryUsed} GB)`}
                    name="memoryLimit"
                    type="number"
                    margin="normal"
                    value={formData.memoryLimit}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    fullWidth
                    label={`Storage Limit (Used: ${quota.storageUsed} GB)`}
                    name="storageLimit"
                    type="number"
                    margin="normal"
                    value={formData.storageLimit}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                />
                <Alert severity="warning" sx={{ mt: 2 }}>
                    Changes will take effect immediately and may impact department services.
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant="outlined">Cancel</Button>
                <Button onClick={handleInternalSave} variant="contained" color="primary" startIcon={<EditIcon />}>
                    Save Limits
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// --- MAIN COMPONENT ---

const ResourceQuotas = () => {
    const theme = useTheme();
    const [quotas, setQuotas] = useState(mockQuotas);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [selectedQuota, setSelectedQuota] = useState(null);

    // --- Aggregated Data Calculation ---
    const aggregatedData = useMemo(() => {
        return quotas.reduce((acc, curr) => {
            acc.totalCpuLimit += curr.cpuLimit;
            acc.totalCpuUsed += curr.cpuUsed;
            acc.totalMemoryLimit += curr.memoryLimit;
            acc.totalMemoryUsed += curr.memoryUsed;
            acc.totalStorageLimit += curr.storageLimit;
            acc.totalStorageUsed += curr.storageUsed;
            return acc;
        }, {
            totalCpuLimit: 0, totalCpuUsed: 0,
            totalMemoryLimit: 0, totalMemoryUsed: 0,
            totalStorageLimit: 0, totalStorageUsed: 0,
        });
    }, [quotas]);

    // --- Handlers ---
    const handleOpenEdit = (quota) => {
        setSelectedQuota(quota);
        setOpenEditDialog(true);
    };

    const handleSaveQuota = (id, newLimits) => {
        setQuotas(prevQuotas => prevQuotas.map(q =>
            q.id === id ? { ...q, ...newLimits } : q
        ));
    };

    // --- Data Grid Columns ---
    const columns = [
        { field: 'department', headerName: 'Department', width: 150, renderCell: (params) => (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <GroupIcon sx={{ mr: 1, fontSize: 18, color: theme.palette.text.secondary }} />
                <Typography fontWeight={600}>{params.value}</Typography>
            </Box>
        )},
        { field: 'cpuUsage', headerName: 'CPU Usage (Cores)', width: 180, renderCell: (params) => (
            <UsageProgressBar used={params.row.cpuUsed} limit={params.row.cpuLimit} theme={theme} />
        )},
        { field: 'memoryUsage', headerName: 'Memory Usage (GB)', width: 180, renderCell: (params) => (
            <UsageProgressBar used={params.row.memoryUsed} limit={params.row.memoryLimit} theme={theme} />
        )},
        { field: 'storageUsage', headerName: 'Storage Usage (GB)', width: 180, renderCell: (params) => (
            <UsageProgressBar used={params.row.storageUsed} limit={params.row.storageLimit} theme={theme} />
        )},
        { 
            field: 'status', 
            headerName: 'Status', 
            width: 120, 
            renderCell: (params) => {
                let color;
                switch (params.value) {
                    case 'Critical': color = 'error'; break;
                    case 'Warning': color = 'warning'; break;
                    default: color = 'success';
                }
                return <Chip label={params.value} color={color} size="small" variant="outlined" />;
            }
        },
        { field: 'users', headerName: 'Users', type: 'number', width: 100 },
        { 
            field: 'action', 
            headerName: 'Action', 
            sortable: false, 
            width: 100, 
            renderCell: (params) => (
                <IconButton 
                    color="primary" 
                    size="small" 
                    onClick={() => handleOpenEdit(params.row)}
                    title={`Edit quotas for ${params.row.department}`}
                >
                    <EditIcon fontSize="inherit" />
                </IconButton>
            )
        },
    ];

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{
                    fontWeight: 800,
                    color: theme.palette.primary.dark,
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2
                }}
            >
                <SettingsIcon sx={{ mr: 1.5, fontSize: '3rem', color: theme.palette.secondary.dark }} />
                Resource Quota Management
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 4 }}>
                Monitor and allocate system resources (CPU, Memory, Storage) by department.
            </Typography>

            {/* --- 1. Aggregated Summary Cards --- */}
            <Grid container spacing={4} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <SummaryCard
                        title="Total CPU Allocation"
                        icon={<SpeedIcon />}
                        totalLimit={aggregatedData.totalCpuLimit}
                        totalUsed={aggregatedData.totalCpuUsed}
                        unit="Cores"
                        color="info"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <SummaryCard
                        title="Total Memory Allocation"
                        icon={<MemoryIcon />}
                        totalLimit={aggregatedData.totalMemoryLimit}
                        totalUsed={aggregatedData.totalMemoryUsed}
                        unit="GB"
                        color="primary"
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={4}>
                    <SummaryCard
                        title="Total Storage Allocation"
                        icon={<StorageIcon />}
                        totalLimit={aggregatedData.totalStorageLimit}
                        totalUsed={aggregatedData.totalStorageUsed}
                        unit="GB"
                        color="secondary"
                    />
                </Grid>
            </Grid>
            
            <Divider sx={{ my: 4 }} />

            {/* --- 2. Detailed Quota Data Grid --- */}
            <Paper elevation={6} sx={{ p: 4, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" fontWeight={700} color="textPrimary">
                        Department Quota Breakdown
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<TrendingUpIcon />}
                        disabled
                    >
                        View Trend Analysis
                    </Button>
                </Box>
                
                <Box sx={{ height: 400, width: '100%' }}>
                    <DataGrid
                        rows={quotas}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 5 } },
                            sorting: { sortModel: [{ field: 'department', sort: 'asc' }] },
                        }}
                        pageSizeOptions={[5, 10, 25]}
                        disableRowSelectionOnClick
                    />
                </Box>
            </Paper>

            {/* --- Edit Quota Dialog --- */}
            <EditQuotaDialog
                open={openEditDialog}
                handleClose={() => setOpenEditDialog(false)}
                quota={selectedQuota}
                handleSave={handleSaveQuota}
            />
        </Container>
    );
};

export default ResourceQuotas;